import React, { FormEvent, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Button, Checkbox, Fade, Icon, Input, Textarea, ToggleHeight } from '@column/column-ui-kit';

import { NotificationList } from '~/components/NotificationList';
import { UnderlineLink } from '~/elements';
import { Option, OptionLabel } from '~/public/pages/_components';
import { WebsiteRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import {
  Headline,
  Hint,
  Inner,
  PlatformInfoDescription,
  PlatformInfoFormElement,
  PlatformInfoFormLabelInline,
  PlatformInfoFormLabelTooltip,
  PlatformInfoHeader,
  SmallHeadline,
} from '~/styles';
import { validateEmail } from '~/util';
import { reCaptchaCheck } from '~/util/reCaptcha';

const StyledNotificationList = styled(NotificationList)`
  scroll-margin-top: 80px;
`;

const Grid = styled.div`
  display: grid;
  grid-gap: 48px;

  @media (max-width: 600px) {
    grid-gap: 32px;
  }
`;

const FormSection = styled.div`
  display: grid;
  grid-gap: 24px;

  @media (min-width: 601px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FormWrapper = styled.div`
  display: grid;
  grid-gap: 16px;
`;

const FormHeader = styled.div`
  display: grid;
  grid-gap: 4px;
`;

const StyledTextarea = styled(Textarea)`
  min-height: 120px;
`;

const OptionList = styled.div`
  display: grid;
  grid-gap: 8px;

  @media (min-width: 601px) {
    grid-gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ComplianceList = styled.div`
  display: grid;
  grid-gap: 8px;

  @media (min-width: 601px) {
    max-width: 60%;
    grid-gap: 12px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ComplianceListOption = styled(Option)<{ isActive?: boolean }>`
  display: flex;
  justify-content: center;
  text-align: center;
  color: ${({ theme }) => theme.secondary.background};

  ${({ isActive }) =>
    isActive &&
    css`
      color: ${({ theme }) => theme.primary.background};
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.primary.background};
    `}
`;

const Action = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const StyledHeadline = styled(Headline)`
  font-size: 28px;
`;

const Success = styled(PlatformInfoHeader)`
  margin: 0;
`;

const CenteredSuccess = styled(Success)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  margin: auto;
`;

const StyledPlatformInfoHeader = styled(PlatformInfoHeader)`
  margin-bottom: 32px;

  @media (max-width: 600px) {
    margin-bottom: 24px;
  }
`;

const BackToWebsite = styled(Button)`
  margin-top: 16px;
`;

const OtherContainer = styled(ToggleHeight)`
  --toggle-height-padding: 12px 0 0 0;
`;

export interface ContactFormProps {
  onSuccess?: () => void;
  origin: 'Website' | 'Dashboard';
  initialValues?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  showBackToWebsite?: boolean;
  hideTitle?: boolean;
}

export interface ContactFormRef {
  submit: () => void;
}

export const ContactForm = forwardRef<ContactFormRef, ContactFormProps>(
  ({ onSuccess, origin, initialValues, showBackToWebsite = true, hideTitle = false }, ref) => {
    const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
    const formElementRef = useRef<HTMLFormElement>(null);
    const [firstName, setFirstName] = useState<string>(initialValues?.firstName ?? '');
    const [lastName, setLastName] = useState<string>(initialValues?.lastName ?? '');
    const [email, setEmail] = useState<string>(initialValues?.email ?? '');
    const [companyName, setCompanyName] = useState<string>('');
    const [companySize, setCompanySize] = useState<string>('');
    const [companyWebsite, setCompanyWebsite] = useState<string>('');
    const [hasFinancialProducts, setHasFinancialProducts] = useState<string>('');
    const [howDidYouHear, setHowDidYouHear] = useState<string[]>([]);
    const [howDidYouHearOther, setHowDidYouHearOther] = useState<string>('');
    const [whatAreYouBuilding, setWhatAreYouBuilding] = useState<string>('');
    const [complianceCapabilities, setComplianceCapabilities] = useState<string>('');
    const [otherComment, setOtherComment] = useState<string>('');
    const [customerTypes, setCustomerTypes] = useState<string[]>([]);
    const [customerTypeOther, setCustomerTypesOther] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { handleReCaptcha } = reCaptchaCheck();

    useImperativeHandle(ref, () => ({
      submit: () => {
        formElementRef.current?.requestSubmit();
      },
    }));

    const handleSubmit = (event: FormEvent) => {
      event.preventDefault();

      if (isLoading) {
        return;
      }

      if (!email || !validateEmail(email)) {
        addDangerNotification({
          content: 'Please enter a valid email address',
          display: origin === 'Website' ? 'page' : 'global',
        });
        return;
      }

      if (!companyWebsite) {
        addDangerNotification({
          content: 'Please enter your company website',
          display: origin === 'Website' ? 'page' : 'global',
        });
        return;
      }

      if (!hasFinancialProducts) {
        addDangerNotification({
          content: 'Please select whether you have financial products live in the US today',
          display: origin === 'Website' ? 'page' : 'global',
        });
        return;
      }

      if (howDidYouHear.length < 1) {
        addDangerNotification({
          content: 'Please select how you heard about us',
          display: origin === 'Website' ? 'page' : 'global',
        });
        return;
      }

      if (!whatAreYouBuilding) {
        addDangerNotification({
          content: 'Please describe what you are building',
          display: origin === 'Website' ? 'page' : 'global',
        });
        return;
      }

      setIsLoading(true);

      const data = {
        origin,
        firstName,
        lastName,
        emailAddress: email,
        companyName: companyName,
        companySize: companySize,
        companyWebsite: companyWebsite,
        hasFinancialProducts: hasFinancialProducts,
        howDidYouHear: howDidYouHear.join(', '),
        howDidYouHearOther: howDidYouHearOther,
        whatAreYouBuilding: whatAreYouBuilding,
        complianceCapabilities: complianceCapabilities,
        otherComment: otherComment,
        customerTypes: customerTypes.join(', '),
        customerTypeOther: customerTypeOther,
      };

      handleReCaptcha(
        (reCaptchaToken: string) =>
          WebsiteRepository.contact(data, reCaptchaToken)
            .then(() => {
              setSubmitted(true);
              onSuccess?.();
            })
            .catch((error) => {
              addDangerNotification({
                content: error.message,
                display: origin === 'Website' ? 'page' : 'global',
              });
            })
            .finally(() => setIsLoading(false)),
        (err) => {
          setIsLoading(false);

          addDangerNotification({
            content: err.message,
            display: origin === 'Website' ? 'page' : 'global',
          });
        }
      );
    };

    const successContent = (
      <>
        <PlatformInfoDescription as="div">
          <p>We look forward to getting connected with you!</p>
          <p>
            You can build anything with Column through our{' '}
            <UnderlineLink href="https://column.com/docs/guides/sandbox-and-testing" newTab withoutArrow>
              sandbox environment
            </UnderlineLink>{' '}
            and learn more about{' '}
            <UnderlineLink href="https://column.com/docs/guides#usecases" newTab withoutArrow>
              all the ways you can partner with us
            </UnderlineLink>
            .
          </p>
        </PlatformInfoDescription>
        {showBackToWebsite && (
          <div>
            <BackToWebsite onClick={() => (window.location.href = 'https://column.com')}>
              Back to column.com
            </BackToWebsite>
          </div>
        )}
      </>
    );

    return (
      <>
        <Fade show={submitted}>
          {origin === 'Dashboard' ? (
            <CenteredSuccess>
              <div>
                <Inner pt={0} px={0} pb={12}>
                  <Headline>Submitted</Headline>
                </Inner>
                {successContent}
              </div>
            </CenteredSuccess>
          ) : (
            <Success>
              <StyledHeadline>Submitted</StyledHeadline>
              {successContent}
            </Success>
          )}
        </Fade>

        <Fade show={!submitted}>
          {!hideTitle && (
            <StyledPlatformInfoHeader>
              <StyledHeadline>Contact us</StyledHeadline>
            </StyledPlatformInfoHeader>
          )}

          <StyledNotificationList display="page" />

          <Grid as="form" ref={formElementRef} onSubmit={handleSubmit}>
            <FormSection>
              <PlatformInfoFormElement>
                <PlatformInfoFormLabelInline>First name</PlatformInfoFormLabelInline>
                <Input placeholder="First name" onChange={(value: string) => setFirstName(value)} value={firstName} />
              </PlatformInfoFormElement>

              <PlatformInfoFormElement>
                <PlatformInfoFormLabelInline>Last name</PlatformInfoFormLabelInline>
                <Input placeholder="Last name" onChange={(value: string) => setLastName(value)} value={lastName} />
              </PlatformInfoFormElement>

              <PlatformInfoFormElement>
                <PlatformInfoFormLabelInline>Your email</PlatformInfoFormLabelInline>
                <Input placeholder="Email address" onChange={(value: string) => setEmail(value)} value={email} />
              </PlatformInfoFormElement>

              <PlatformInfoFormElement>
                <PlatformInfoFormLabelInline>Company Website</PlatformInfoFormLabelInline>
                <Input
                  placeholder="https://example.com"
                  onChange={(value: string) => setCompanyWebsite(value)}
                  value={companyWebsite}
                />
              </PlatformInfoFormElement>

              <PlatformInfoFormElement>
                <PlatformInfoFormLabelInline>
                  Company name <small>(Optional)</small>
                  <PlatformInfoFormLabelTooltip content="The name of the company you work or are representing for this financial product">
                    <Icon.CircleQuestionmark />
                  </PlatformInfoFormLabelTooltip>
                </PlatformInfoFormLabelInline>
                <Input
                  placeholder="Company name"
                  onChange={(value: string) => setCompanyName(value)}
                  value={companyName}
                />
              </PlatformInfoFormElement>

              <PlatformInfoFormElement>
                <PlatformInfoFormLabelInline>
                  Number of employees <small>(Optional)</small>
                  <PlatformInfoFormLabelTooltip content="The number of people who work at the company">
                    <Icon.CircleQuestionmark />
                  </PlatformInfoFormLabelTooltip>
                </PlatformInfoFormLabelInline>
                <Input placeholder="50" onChange={(value: string) => setCompanySize(value)} value={companySize} />
              </PlatformInfoFormElement>
            </FormSection>

            <FormWrapper>
              <FormHeader>
                <SmallHeadline>Do you have financial products live in the US today?</SmallHeadline>
              </FormHeader>

              <ComplianceList>
                <ComplianceListOption
                  isSimple
                  isActive={hasFinancialProducts === 'Yes'}
                  onClick={() => setHasFinancialProducts('Yes')}
                >
                  <OptionLabel>Yes</OptionLabel>
                </ComplianceListOption>
                <ComplianceListOption
                  isSimple
                  isActive={hasFinancialProducts === 'No'}
                  onClick={() => setHasFinancialProducts('No')}
                >
                  <OptionLabel>No</OptionLabel>
                </ComplianceListOption>
              </ComplianceList>
            </FormWrapper>

            <FormWrapper>
              <FormHeader>
                <SmallHeadline>
                  Who are your customers? <small>(optional)</small>
                </SmallHeadline>
                <Hint>(Select all that apply)</Hint>
              </FormHeader>

              <div>
                <OptionList>
                  {['Consumers', 'Sole Proprietorships', 'SMBs', 'Large Enterprises', 'Other'].map((option: string) => (
                    <Option
                      key={option}
                      isSimple
                      isActive={customerTypes.includes(option)}
                      onClick={() =>
                        setCustomerTypes((state) =>
                          customerTypes.includes(option) ? state.filter((i) => i !== option) : [...state, option]
                        )
                      }
                    >
                      <Checkbox
                        isChecked={customerTypes.includes(option)}
                        onCheckedChange={() =>
                          setCustomerTypes((state) =>
                            customerTypes.includes(option) ? state.filter((i) => i !== option) : [...state, option]
                          )
                        }
                        label={option}
                      />
                    </Option>
                  ))}
                </OptionList>

                <OtherContainer isClose={!customerTypes.includes('Other')}>
                  <PlatformInfoFormElement onClick={(e) => e.stopPropagation()}>
                    <StyledTextarea
                      placeholder="Other customer types"
                      onChange={(value: string) => setCustomerTypesOther(value)}
                      value={customerTypeOther}
                    />
                  </PlatformInfoFormElement>
                </OtherContainer>
              </div>
            </FormWrapper>

            <FormWrapper>
              <FormHeader>
                <SmallHeadline>How did you hear about us?</SmallHeadline>
              </FormHeader>

              <div>
                <OptionList>
                  {[
                    'Google search',
                    'ChatGPT/ Claude/ other AI model',
                    'Conference',
                    'Investor',
                    'Podcast',
                    'Newsletter',
                    'Consultant',
                    'Referral (Network, Processor, etc.)',
                    'Other',
                  ].map((option: string) => (
                    <Option
                      key={option}
                      isSimple
                      isActive={howDidYouHear.includes(option)}
                      onClick={() =>
                        setHowDidYouHear((state) =>
                          howDidYouHear.includes(option) ? state.filter((i) => i !== option) : [...state, option]
                        )
                      }
                    >
                      <Checkbox
                        isChecked={howDidYouHear.includes(option)}
                        onCheckedChange={() =>
                          setHowDidYouHear((state) =>
                            howDidYouHear.includes(option) ? state.filter((i) => i !== option) : [...state, option]
                          )
                        }
                        label={option}
                      />
                    </Option>
                  ))}
                </OptionList>

                <OtherContainer isClose={!howDidYouHear.includes('Other')}>
                  <PlatformInfoFormElement onClick={(e) => e.stopPropagation()}>
                    <StyledTextarea
                      placeholder="Please share more details"
                      onChange={(value: string) => setHowDidYouHearOther(value)}
                      value={howDidYouHearOther}
                    />
                  </PlatformInfoFormElement>
                </OtherContainer>
              </div>
            </FormWrapper>

            <FormWrapper>
              <FormHeader>
                <SmallHeadline>What are you building?</SmallHeadline>
              </FormHeader>
              <StyledTextarea
                placeholder="Please describe your product or service and how you plan to use Column's banking infrastructure"
                onChange={(value: string) => setWhatAreYouBuilding(value)}
                value={whatAreYouBuilding}
              />
            </FormWrapper>

            <FormWrapper>
              <FormHeader>
                <SmallHeadline>
                  Do you have a compliance function or compliance&nbsp;capabilities?
                  <PlatformInfoFormLabelTooltip
                    content="Compliance helps ensure adherence to applicable laws, regulations and internal policies and procedures"
                    largeMargin
                  >
                    <Icon.CircleQuestionmark />
                  </PlatformInfoFormLabelTooltip>
                </SmallHeadline>
              </FormHeader>

              <ComplianceList>
                <ComplianceListOption
                  isSimple
                  isActive={complianceCapabilities === 'Yes'}
                  onClick={() => setComplianceCapabilities('Yes')}
                >
                  <OptionLabel>Yes</OptionLabel>
                </ComplianceListOption>
                <ComplianceListOption
                  isSimple
                  isActive={complianceCapabilities === 'No'}
                  onClick={() => setComplianceCapabilities('No')}
                >
                  <OptionLabel>No</OptionLabel>
                </ComplianceListOption>
                <ComplianceListOption
                  isSimple
                  isActive={complianceCapabilities === "I'm not sure"}
                  onClick={() => setComplianceCapabilities("I'm not sure")}
                >
                  <OptionLabel>I'm not sure</OptionLabel>
                </ComplianceListOption>
              </ComplianceList>
            </FormWrapper>

            <FormWrapper>
              <FormHeader>
                <SmallHeadline>Please include any other questions or comments you&nbsp;may&nbsp;have.</SmallHeadline>
              </FormHeader>
              <StyledTextarea
                placeholder="Comments"
                onChange={(value: string) => setOtherComment(value)}
                value={otherComment}
              />
            </FormWrapper>

            <Action>
              <Button isLoading={isLoading}>Submit</Button>
            </Action>
          </Grid>
        </Fade>
      </>
    );
  }
);

ContactForm.displayName = 'ContactForm';
